import "@/App.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { FavoritesProvider } from "@/context/FavoritesContext";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Home from "@/pages/Home";
import Browse from "@/pages/Browse";
import ListingDetail from "@/pages/ListingDetail";
import CreateListing from "@/pages/CreateListing";
import Dashboard from "@/pages/Dashboard";
import Messages from "@/pages/Messages";
import BookingCheckout from "@/pages/BookingCheckout";
import CheckoutReturn from "@/pages/CheckoutReturn";
import Login from "@/pages/Login";
import Profile from "@/pages/Profile";
import Admin from "@/pages/Admin";
import ResetPassword from "@/pages/ResetPassword";
import About from "@/pages/About";
import HowItWorks from "@/pages/HowItWorks";
import FAQ from "@/pages/FAQ";
import Contact from "@/pages/Contact";
import Policy from "@/pages/Policy";
import OwnerProtection from "@/pages/OwnerProtection";
import Footer from "@/components/Footer";

const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash;
    const sessionId = new URLSearchParams(hash.replace("#", "")).get("session_id");
    (async () => {
      try {
        const res = await api.post("/auth/session", { session_id: sessionId });
        setUser(res.data);
        if (localStorage.getItem("sellend_pending_consent")) {
          try { await api.post("/policy/consent", { version: "1.0" }); } catch {}
          localStorage.removeItem("sellend_pending_consent");
        }
        window.location.hash = "";
        navigate("/dashboard", { replace: true });
      } catch {
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-[#FF5A00] border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="mt-4 text-muted-foreground font-medium">Signing you in…</p>
      </div>
    </div>
  );
};

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 border-4 border-[#FF5A00] border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
      <Navbar />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/browse" element={<Browse />} />
        <Route path="/listing/:id" element={<ListingDetail />} />
        <Route path="/user/:userId" element={<Profile />} />
        <Route path="/about" element={<About />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/policy" element={<Policy />} />
        <Route path="/owner-protection" element={<OwnerProtection />} />
        <Route path="/login" element={<Login />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/create" element={<Protected><CreateListing /></Protected>} />
        <Route path="/edit/:id" element={<Protected><CreateListing /></Protected>} />
        <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
        <Route path="/messages" element={<Protected><Messages /></Protected>} />
        <Route path="/messages/:otherId" element={<Protected><Messages /></Protected>} />
        <Route path="/admin" element={<Protected><Admin /></Protected>} />
        <Route path="/booking/:bookingId" element={<Protected><BookingCheckout /></Protected>} />
        <Route path="/checkout-return" element={<Protected><CheckoutReturn /></Protected>} />
      </Routes>
      <Footer />
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-center" richColors />
          <FavoritesProvider>
            <AppRouter />
          </FavoritesProvider>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
